import * as XLSX from "xlsx";
import type {
  ClassListItem,
  ClassSession,
  SessionParticipant,
  UserListItem,
} from "types/domain";

export type Translate = (
  key: string,
  params?: Record<string, string | number>
) => string;

export interface ClassSessionWithMeta extends ClassSession {
  class_name: string;
  class_code: string | null;
}

export interface ExportHistoryParams {
  fromDate: string;
  toDate: string;
  classes: ClassListItem[];
  sessions: ClassSessionWithMeta[];
  participantsBySession: Record<string, SessionParticipant[]>;
  students: UserListItem[];
  studentsByClassId: Record<string, UserListItem[]>;
  /**
   * When true, the export aggregates every student enrolled in any class
   * (used by the "export all students in the center" flow). When false,
   * only students who actually attended at least one session in the
   * range are included per sheet.
   */
  includeAllEnrolled: boolean;
  /** Selected month in YYYY-MM format; used to render day-of-month columns. */
  month: string;
  /** Sheet headers follow the UI language. */
  t: Translate;
}

// Indexed by Date#getDay() (0 = Sunday).
const WEEKDAY_KEYS = [
  "excelExport.weekday.sun",
  "excelExport.weekday.mon",
  "excelExport.weekday.tue",
  "excelExport.weekday.wed",
  "excelExport.weekday.thu",
  "excelExport.weekday.fri",
  "excelExport.weekday.sat",
];

const getMonthInfo = (month: string) => {
  const [yearText, monthText] = month.split("-");
  const year = Number(yearText);
  const monthIndex = Number(monthText) - 1;
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  return { year, monthIndex, daysInMonth };
};

const formatTime = (value: string | null | undefined): string => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${String(date.getHours()).padStart(2, "0")}:${String(
    date.getMinutes()
  ).padStart(2, "0")}`;
};

const getBirthYear = (dob?: string | null): string => {
  if (!dob) return "";
  const date = new Date(dob);
  if (Number.isNaN(date.getTime())) return "";
  return String(date.getFullYear());
};

const formatClassHeader = (
  cls: ClassListItem,
  t: Translate,
  firstSession?: ClassSessionWithMeta
): string => {
  const code = cls.class_code || cls.name || t("excelExport.classFallback");
  const time =
    firstSession && firstSession.start_time && firstSession.end_time
      ? `${formatTime(firstSession.start_time)}-${formatTime(
          firstSession.end_time
        )}`
      : "";
  return time ? `${code} "${time}"` : code;
};

type CellStyle = NonNullable<XLSX.CellObject["s"]>;
type Side = NonNullable<NonNullable<CellStyle["border"]>["top"]>;
const side = (style: Side["style"], color: string): Side => ({
  style,
  color: { rgb: color },
});

const THIN: Side = side("thin", "FF8E8E8E");
const MEDIUM: Side = side("medium", "FF305496");

const thinBorder: CellStyle["border"] = {
  top: THIN,
  bottom: THIN,
  left: THIN,
  right: THIN,
};

const titleBorder: CellStyle["border"] = {
  top: MEDIUM,
  bottom: THIN,
  left: MEDIUM,
  right: THIN,
};

const titleEndBorder: CellStyle["border"] = {
  top: MEDIUM,
  bottom: THIN,
  left: THIN,
  right: MEDIUM,
};

const titleMiddleBorder: CellStyle["border"] = {
  top: MEDIUM,
  bottom: THIN,
  left: THIN,
  right: THIN,
};

const dataRowBorder: CellStyle["border"] = {
  top: THIN,
  bottom: THIN,
  left: THIN,
  right: THIN,
};

const lastRowBorder: CellStyle["border"] = {
  top: THIN,
  bottom: MEDIUM,
  left: THIN,
  right: THIN,
};

const lastRowStartBorder: CellStyle["border"] = {
  top: THIN,
  bottom: MEDIUM,
  left: MEDIUM,
  right: THIN,
};

const lastRowEndBorder: CellStyle["border"] = {
  top: THIN,
  bottom: MEDIUM,
  left: THIN,
  right: MEDIUM,
};

// Color palette
const TITLE_FILL: CellStyle["fill"] = {
  patternType: "solid",
  fgColor: { rgb: "FF305496" },
};
const SUB_HEADER_FILL: CellStyle["fill"] = {
  patternType: "solid",
  fgColor: { rgb: "FF8EA9DB" },
};
const SUB_HEADER_FILL_LIGHT: CellStyle["fill"] = {
  patternType: "solid",
  fgColor: { rgb: "FFD9E1F2" },
};
const WEEKEND_FILL: CellStyle["fill"] = {
  patternType: "solid",
  fgColor: { rgb: "FFFCE4D6" },
};
const WEEKEND_HEADER_FILL: CellStyle["fill"] = {
  patternType: "solid",
  fgColor: { rgb: "FFF8CBAD" },
};
const ZEBRA_FILL: CellStyle["fill"] = {
  patternType: "solid",
  fgColor: { rgb: "FFF2F2F2" },
};
const TOTAL_FILL: CellStyle["fill"] = {
  patternType: "solid",
  fgColor: { rgb: "FFFFF2CC" },
};

const buildStyle = (overrides: Partial<CellStyle>): CellStyle => ({
  border: thinBorder,
  alignment: { vertical: "center", horizontal: "center" },
  ...overrides,
});

const buildSheetForClass = (
  cls: ClassListItem,
  classSessions: ClassSessionWithMeta[],
  participantsBySession: Record<string, SessionParticipant[]>,
  students: UserListItem[],
  includeAllEnrolled: boolean,
  monthInfo: ReturnType<typeof getMonthInfo>,
  t: Translate
): XLSX.WorkSheet => {
  const { year, monthIndex, daysInMonth } = monthInfo;

  // Build a map: day-of-month -> Set of student user_ids that attended
  const attendedByDay: Record<number, Set<string>> = {};
  for (let d = 1; d <= daysInMonth; d += 1) attendedByDay[d] = new Set();
  classSessions.forEach((s) => {
    if (!s.start_time) return;
    const d = new Date(s.start_time);
    if (Number.isNaN(d.getTime())) return;
    if (d.getFullYear() !== year || d.getMonth() !== monthIndex) return;
    const day = d.getDate();
    const parts = participantsBySession[s.id] ?? [];
    parts.forEach((p) => {
      if (
        p.role === "student" &&
        (p.status === "checked_in" || p.status === "checked_out")
      ) {
        attendedByDay[day].add(p.user_id);
      }
    });
  });

  // Decide which students to include
  const attendedStudentIds = new Set<string>();
  Object.values(attendedByDay).forEach((set) => {
    set.forEach((id) => attendedStudentIds.add(id));
  });
  const targetStudents = includeAllEnrolled
    ? students
    : students.filter((s) => attendedStudentIds.has(s.id));
  targetStudents.sort((a, b) => a.full_name.localeCompare(b.full_name, "vi"));

  const firstSession = [...classSessions].sort((a, b) => {
    const left = a.start_time ? new Date(a.start_time).getTime() : 0;
    const right = b.start_time ? new Date(b.start_time).getTime() : 0;
    return left - right;
  })[0];
  const headerClassLine = formatClassHeader(cls, t, firstSession);

  // Column indices: STT | TÊN HỌC VIÊN | NĂM SINH | day1..N | Tổng buổi
  const COL_STT = 0;
  const COL_NAME = 1;
  const COL_DOB = 2;
  const COL_DAY_START = 3;
  const COL_DAY_END = COL_DAY_START + daysInMonth - 1;
  const COL_TOTAL = COL_DAY_END + 1;
  const lastCol = COL_TOTAL;
  const lastDataRow = 2 + targetStudents.length;

  const sheet: XLSX.WorkSheet = {};
  const setCell = (
    r: number,
    c: number,
    value: string | number,
    style: CellStyle
  ) => {
    const cellRef = XLSX.utils.encode_cell({ r, c });
    sheet[cellRef] = {
      t: typeof value === "number" ? "n" : "s",
      v: value,
      s: style,
    };
  };

  // Per-day metadata
  const dayMeta: { day: number; weekday: string; isWeekend: boolean }[] = [];
  for (let d = 1; d <= daysInMonth; d += 1) {
    const weekdayIdx = new Date(year, monthIndex, d).getDay();
    dayMeta.push({
      day: d,
      weekday: t(WEEKDAY_KEYS[weekdayIdx]),
      isWeekend: weekdayIdx === 0 || weekdayIdx === 6,
    });
  }

  // Header style definitions
  const titleLeftStyle = buildStyle({
    font: { bold: true, color: { rgb: "FFFFFFFF" }, sz: 14 },
    fill: TITLE_FILL,
    border: titleBorder,
    alignment: { horizontal: "center", vertical: "center" },
  });
  const titleMidStyle = buildStyle({
    font: { bold: true, color: { rgb: "FFFFFFFF" }, sz: 13 },
    fill: TITLE_FILL,
    border: titleMiddleBorder,
    alignment: { horizontal: "center", vertical: "center" },
  });
  const titleRightStyle = buildStyle({
    font: { bold: true, color: { rgb: "FFFFFFFF" }, sz: 14 },
    fill: TITLE_FILL,
    border: titleEndBorder,
    alignment: { horizontal: "center", vertical: "center" },
  });

  const monthLabel = t("excelExport.monthProgress", {
    month: String(monthIndex + 1).padStart(2, "0"),
    year,
  });

  // Row 0
  //   - A0:B0 merged = LỚP title (2 cols wide, 1 row tall)
  //   - C0 = (empty, will be styled for next row header)
  //   - D0:lastDayCol merged = TIẾN TRÌNH THÁNG
  //   - Last col (Tổng buổi) set later with totalHeaderStyle
  setCell(
    0,
    COL_STT,
    t("excelExport.classTitle", { name: headerClassLine }),
    titleLeftStyle
  );
  setCell(0, COL_NAME, "", titleRightStyle);
  setCell(0, COL_DOB, "", titleMidStyle);
  setCell(0, COL_DAY_START, monthLabel, titleMidStyle);
  for (let c = COL_DAY_START + 1; c < COL_DAY_END; c += 1) {
    setCell(0, c, "", titleMidStyle);
  }
  setCell(0, COL_DAY_END, "", titleRightStyle);

  // Row 1
  const subHeaderLeftStyle = buildStyle({
    font: { bold: true, sz: 11, color: { rgb: "FFFFFFFF" } },
    fill: SUB_HEADER_FILL,
    alignment: { horizontal: "center", vertical: "center" },
  });
  setCell(1, COL_STT, "", subHeaderLeftStyle);
  setCell(1, COL_NAME, t("excelExport.studentName"), subHeaderLeftStyle);
  setCell(1, COL_DOB, t("excelExport.birthYear"), subHeaderLeftStyle);
  dayMeta.forEach((meta, idx) => {
    const col = COL_DAY_START + idx;
    const style = meta.isWeekend
      ? buildStyle({
          font: { bold: true, sz: 10, color: { rgb: "FF833C0C" } },
          fill: WEEKEND_HEADER_FILL,
          alignment: { horizontal: "center", vertical: "center" },
        })
      : buildStyle({
          font: { bold: true, sz: 10, color: { rgb: "FFFFFFFF" } },
          fill: SUB_HEADER_FILL,
          alignment: { horizontal: "center", vertical: "center" },
        });
    setCell(1, col, meta.weekday, style);
  });

  // Row 2
  const subHeaderLightLeftStyle = buildStyle({
    font: { bold: true, sz: 11, color: { rgb: "FF1F4E79" } },
    fill: SUB_HEADER_FILL_LIGHT,
    alignment: { horizontal: "center", vertical: "center" },
  });
  setCell(2, COL_STT, t("excelExport.rowNumber"), subHeaderLightLeftStyle);
  setCell(2, COL_NAME, "", subHeaderLightLeftStyle);
  setCell(2, COL_DOB, "", subHeaderLightLeftStyle);
  dayMeta.forEach((meta, idx) => {
    const col = COL_DAY_START + idx;
    const style = meta.isWeekend
      ? buildStyle({
          font: { bold: true, sz: 10, color: { rgb: "FF833C0C" } },
          fill: WEEKEND_HEADER_FILL,
          alignment: { horizontal: "center", vertical: "center" },
        })
      : buildStyle({
          font: { bold: true, sz: 10, color: { rgb: "FF1F4E79" } },
          fill: SUB_HEADER_FILL_LIGHT,
          alignment: { horizontal: "center", vertical: "center" },
        });
    setCell(2, col, String(meta.day).padStart(2, "0"), style);
  });

  // Tổng buổi header (merged across rows 0-2 at COL_TOTAL)
  const totalHeaderStyle = buildStyle({
    font: { bold: true, sz: 12, color: { rgb: "FFC00000" } },
    fill: TOTAL_FILL,
    border: titleEndBorder,
    alignment: { horizontal: "center", vertical: "center" },
  });
  setCell(0, COL_TOTAL, t("excelExport.totalSessions"), totalHeaderStyle);

  // Data row styles
  const sttStyle = buildStyle({
    font: { sz: 10, color: { rgb: "FF595959" } },
    alignment: { horizontal: "center", vertical: "center" },
  });
  const nameStyle = buildStyle({
    font: { sz: 11, color: { rgb: "FF1F4E79" } },
    alignment: { horizontal: "left", vertical: "center", indent: 1 },
  });
  const dobStyle = buildStyle({
    font: { sz: 10, color: { rgb: "FF595959" } },
    alignment: { horizontal: "center", vertical: "center" },
  });
  const dayDataStyle = buildStyle({
    font: { sz: 11, bold: true, color: { rgb: "FF2E7D32" } },
    alignment: { horizontal: "center", vertical: "center" },
  });
  const totalStyle = buildStyle({
    font: { bold: true, sz: 12, color: { rgb: "FFC00000" } },
    fill: TOTAL_FILL,
    alignment: { horizontal: "center", vertical: "center" },
  });
  const weekendDataStyle = buildStyle({
    font: { sz: 11, bold: true, color: { rgb: "FF2E7D32" } },
    fill: WEEKEND_FILL,
    alignment: { horizontal: "center", vertical: "center" },
  });

  const buildCellStyle = (
    r: number,
    c: number,
    base: CellStyle,
    fill?: CellStyle["fill"]
  ): CellStyle => {
    const isLastRow = r === lastDataRow;
    const isFirstCol = c === COL_STT;
    const isLastCol = c === lastCol;
    let border: CellStyle["border"];
    if (isLastRow && isFirstCol) border = lastRowStartBorder;
    else if (isLastRow && isLastCol) border = lastRowEndBorder;
    else if (isLastRow) border = lastRowBorder;
    else border = dataRowBorder;
    return {
      ...base,
      border,
      ...(fill ? { fill } : {}),
    };
  };

  targetStudents.forEach((student, idx) => {
    const r = 3 + idx;
    const isZebra = idx % 2 === 1;
    const stripeFill = isZebra ? ZEBRA_FILL : undefined;
    const nameFill = isZebra ? ZEBRA_FILL : undefined;
    const totalFill = isZebra ? ZEBRA_FILL : TOTAL_FILL;

    setCell(
      r,
      COL_STT,
      idx + 1,
      buildCellStyle(r, COL_STT, sttStyle, stripeFill)
    );
    setCell(
      r,
      COL_NAME,
      student.full_name,
      buildCellStyle(r, COL_NAME, nameStyle, nameFill)
    );
    setCell(
      r,
      COL_DOB,
      getBirthYear(student.dob),
      buildCellStyle(r, COL_DOB, dobStyle, stripeFill)
    );

    let total = 0;
    for (let d = 1; d <= daysInMonth; d += 1) {
      const col = COL_DAY_START + d - 1;
      const meta = dayMeta[d - 1];
      const present = attendedByDay[d].has(student.id);
      if (present) total += 1;
      const base = meta.isWeekend ? weekendDataStyle : dayDataStyle;
      const fill = meta.isWeekend ? WEEKEND_FILL : stripeFill;
      setCell(r, col, present ? "X" : "", buildCellStyle(r, col, base, fill));
    }
    setCell(
      r,
      COL_TOTAL,
      total,
      buildCellStyle(r, COL_TOTAL, totalStyle, totalFill)
    );
  });

  // Set worksheet range
  sheet["!ref"] = XLSX.utils.encode_range({
    s: { r: 0, c: 0 },
    e: { r: lastDataRow, c: lastCol },
  });

  // Column widths
  const colWidths: XLSX.ColInfo[] = [{ wch: 6 }, { wch: 28 }, { wch: 11 }];
  for (let i = 0; i < daysInMonth; i += 1) {
    colWidths.push({ wch: dayMeta[i].isWeekend ? 3.8 : 3.5 });
  }
  colWidths.push({ wch: 11 });
  sheet["!cols"] = colWidths;

  // Row heights
  sheet["!rows"] = [
    { hpt: 30 },
    { hpt: 22 },
    { hpt: 22 },
    ...targetStudents.map(() => ({ hpt: 22 })),
  ];

  // Merges:
  // 1) LỚP title: row 0, cols 0-1 (STT + TÊN)
  // 2) TIẾN TRÌNH THÁNG MM/YYYY: row 0, day cols
  // 3) Tổng buổi: rows 0-2, last col
  const merges: XLSX.Range[] = [];
  merges.push({
    s: { r: 0, c: COL_STT },
    e: { r: 0, c: COL_NAME },
  });
  merges.push({
    s: { r: 0, c: COL_DAY_START },
    e: { r: 0, c: COL_DAY_END },
  });
  merges.push({
    s: { r: 0, c: COL_TOTAL },
    e: { r: 2, c: COL_TOTAL },
  });
  sheet["!merges"] = merges;

  // Freeze first 3 columns and first 3 rows
  sheet["!freeze"] = { xSplit: 3, ySplit: 3 };

  return sheet;
};

export const buildAttendanceHistoryWorkbook = (
  params: ExportHistoryParams
): XLSX.WorkBook => {
  const wb = XLSX.utils.book_new();
  const {
    classes,
    sessions,
    participantsBySession,
    students,
    studentsByClassId,
    month,
  } = params;
  const monthInfo = getMonthInfo(month);

  const sessionsByClassId = new Map<string, ClassSessionWithMeta[]>();
  sessions.forEach((s) => {
    if (!s.class_id) return;
    const list = sessionsByClassId.get(s.class_id) ?? [];
    list.push(s);
    sessionsByClassId.set(s.class_id, list);
  });

  const sheetNames = new Set<string>();
  let sheetIndex = 1;

  classes.forEach((cls) => {
    if (!cls.id) return;
    const classSessions = sessionsByClassId.get(cls.id) ?? [];
    if (classSessions.length === 0) return;

    const classStudents = studentsByClassId[cls.id] ?? students;
    const sheet = buildSheetForClass(
      cls,
      classSessions,
      participantsBySession,
      classStudents,
      params.includeAllEnrolled,
      monthInfo,
      params.t
    );

    let baseName = (
      cls.class_code ||
      cls.name ||
      params.t("excelExport.sheetClassFallback", { index: sheetIndex })
    )
      .replace(/[\\/?*[\]:]/g, "")
      .slice(0, 28);
    let name =
      baseName || params.t("excelExport.sheetFallback", { index: sheetIndex });

    let suffix = 1;
    while (sheetNames.has(name)) {
      name = `${baseName} (${suffix})`.slice(0, 31);
      suffix += 1;
    }
    sheetNames.add(name);
    XLSX.utils.book_append_sheet(wb, sheet, name);
    sheetIndex += 1;
  });

  return wb;
};

export const downloadWorkbook = (wb: XLSX.WorkBook, filename: string) => {
  XLSX.writeFile(wb, filename);
};

export const generateHistoryFilename = (month: string): string => {
  const safe = (s: string) => s.replace(/[^0-9-]/g, "");
  return `lich-su-hoc_${safe(month)}.xlsx`;
};
